/**
 * Transient auth failure classification + retry (spec: authentication AC-2.7–AC-2.8).
 *
 * Supabase auth calls occasionally receive a non-JSON (HTML) body — e.g. a CDN
 * error/challenge page between the app and the auth provider. Hermes/WebKit surface
 * this as "JSON Parse error: Unexpected character: <"; plain connectivity drops
 * surface as "Network request failed" / "Failed to fetch". These are retryable and
 * must never reach the user as raw strings.
 */

export const TRANSIENT_AUTH_ERROR_MESSAGE =
  'We hit a temporary connection issue. Please try again.'

/** supabase-js error name for fetch-level failures it considers retryable. */
const AUTH_RETRYABLE_FETCH_ERROR_NAME = 'AuthRetryableFetchError'

/**
 * Lower-cased substrings of fetch/parse failure messages across Hermes, WebKit, and
 * supabase-js. Deliberately excludes "timed out" so the CAPTCHA-specific copy from
 * `signUpWithTimeout` keeps precedence.
 */
const TRANSIENT_MESSAGE_PATTERNS = [
  'json parse error',
  'unexpected character',
  'unexpected token',
  'network request failed',
  'failed to fetch',
  'load failed',
  'network connection was lost',
] as const

export function isTransientAuthError(error: unknown): boolean {
  if (error === null || error === undefined) return false
  const err = error as { name?: string; message?: string }
  if (err.name === AUTH_RETRYABLE_FETCH_ERROR_NAME) return true
  const message = (err.message ?? '').toLowerCase()
  return TRANSIENT_MESSAGE_PATTERNS.some((pattern) => message.includes(pattern))
}

const RETRY_DELAY_MS = 600

/**
 * Runs `fn`; when it throws a transient auth error, retries once after a short delay.
 * Non-transient errors and a second transient failure are rethrown to the caller.
 * Callers whose SDK returns `{ error }` instead of throwing must throw the transient
 * error inside `fn` for the retry to trigger.
 */
export async function withTransientAuthRetry<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn()
  } catch (error) {
    if (!isTransientAuthError(error)) throw error
    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS))
    return fn()
  }
}
